import { useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Award, BarChart2, Book, Calendar, ChevronRight, CreditCard, Download,
  Gift, Heart, HelpCircle, LogOut, Map, MapPin, Radio, Repeat, Settings, Shield,
  Star, Tag, Target, Tent, TrendingUp, User2, Users,
} from "lucide-react";
import { useAuth } from "@/lib/auth";

// Profile screen. Mirrors the mobile profile.tsx 1:1 in layout + menu
// composition, but with the web-only routes stubbed with a graceful
// "coming soon" toast (through no-op onClick) instead of the native
// premium-upsell modal — those live on mobile.
export default function Profile() {
  const { user, logout, currentMode, isCamper, isOwner, isAdmin, api, refresh } = useAuth();
  const navigate = useNavigate();

  const displayName = user?.display_name || user?.name || "Camper";
  const email = user?.email || "—";
  const avatar = (currentMode === "owner" ? user?.avatar_owner : user?.avatar_camper) || user?.avatar_data || null;
  const isPremium = !!user?.is_premium;

  // Level + XP progress (identical math to Passport home)
  const nextXp = user?.next_level_xp;
  const levelMin = user?.level_min_xp ?? 0;
  const xp = user?.xp ?? 0;
  const progressPct = useMemo(() => {
    if (!nextXp) return 100;
    const span = Math.max(1, nextXp - levelMin);
    return Math.min(100, Math.round(((xp - levelMin) / span) * 100));
  }, [xp, levelMin, nextXp]);

  const doLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  // Role-specific main menu — matches the mobile app's split.
  const roleMenu = currentMode === "owner"
    ? [
        { icon: MapPin,      label: "Manage Campsites", to: "/owner" },
        { icon: BarChart2,   label: "Analytics",        to: "/owner/analytics", premiumOnly: true },
        { icon: Calendar,    label: "Events",           to: "/owner/events",    premiumOnly: true },
        { icon: Users,       label: "Leaderboard",      to: "/leaderboards" },
        { icon: CreditCard,  label: "Transactions",     to: "/pricing" },
      ]
    : [
        { icon: Book,        label: "My Passport",      to: "/passport" },
        { icon: Award,       label: "My Achievements",  to: "/achievements" },
        { icon: Users,       label: "Leaderboard",      to: "/leaderboards" },
        { icon: Heart,       label: "Saved Campsites",  to: "/explore" },
        { icon: CreditCard,  label: "Transactions",     to: "/pricing" },
      ];

  // Premium features section — gated by is_premium. Non-premium users see
  // the section with a "Premium" pill on every item and clicks go to the
  // pricing page instead of the feature.
  const premiumMenu = [
    { icon: Map,        label: "Adventure Map",  to: "/explore" },  // TODO(phase-7): dedicated map view
    { icon: Target,     label: "Challenges",     to: "/challenges" },
    { icon: Download,   label: "Export Passport", to: null, soon: true },
    { icon: TrendingUp, label: "Insights",       to: null, soon: true },
    { icon: Gift,       label: "Rewards",        to: null, soon: true },
  ];

  const commonMenu = [
    ...(isAdmin ? [{ icon: Shield, label: "Admin Panel", to: "/admin" }] : []),
    { icon: Settings,    label: "Settings",       to: null, soon: true },
    { icon: HelpCircle,  label: "Help & Support", to: "/contact" },
  ];

  return (
    <div className="flex flex-col gap-5 md:gap-6" data-testid="profile-page">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl md:text-3xl font-extrabold" style={{ color: "var(--on-surface)" }}>Profile</h1>
      </div>

      {/* Identity + level card — full width. Tapping anywhere on the
          card opens the Edit Profile screen. */}
      <button
        type="button"
        onClick={() => navigate("/profile/edit")}
        className="card-elev p-5 md:p-6 flex flex-col gap-5 text-left hover:-translate-y-0.5 transition"
        data-testid="profile-identity">
        <div className="flex items-center gap-4">
          {avatar ? (
            <img src={avatar} alt="" className="w-16 h-16 md:w-20 md:h-20 rounded-full object-cover ring-2"
                 style={{ ringColor: "var(--brand)" }} />
          ) : (
            <div className="w-16 h-16 md:w-20 md:h-20 rounded-full flex items-center justify-center text-2xl font-extrabold"
                 style={{ background: "var(--brand)", color: "white" }}>
              {(displayName[0] || "M").toUpperCase()}
            </div>
          )}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <div className="text-xl md:text-2xl font-extrabold truncate" style={{ color: "var(--on-surface)" }}
                   data-testid="profile-name">
                {displayName}
              </div>
              {isPremium && (
                <span className="inline-flex items-center gap-1 text-[10px] font-extrabold uppercase tracking-widest px-2 py-0.5 rounded-full"
                      style={{ background: "color-mix(in srgb, var(--premium) 15%, transparent)", color: "var(--premium)" }}
                      data-testid="profile-premium-pill">
                  <Star className="w-2.5 h-2.5" fill="currentColor" /> Premium
                </span>
              )}
              {user?.is_anonymous && (
                <span className="inline-flex items-center gap-1 text-[10px] font-extrabold uppercase tracking-widest px-2 py-0.5 rounded-full"
                      style={{ background: "rgba(0,0,0,0.12)", color: "var(--on-surface)" }}
                      data-testid="profile-anon-pill">
                  🥸 Anonymous
                </span>
              )}
            </div>
            <div className="text-sm mt-0.5" style={{ color: "var(--on-surface-muted)" }} data-testid="profile-email">
              {email}
            </div>
            <div className="text-[11px] font-mono mt-0.5" style={{ color: "var(--on-surface-muted)" }}>
              {user?.myap_id || ""}
            </div>
          </div>
        </div>

        {/* Level + progress bar */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <div className="inline-flex items-center gap-1.5 text-[11px] font-extrabold uppercase tracking-widest"
                 style={{ color: "var(--brand)" }} data-testid="profile-level">
              <Award className="w-3.5 h-3.5" />
              Lvl {user?.level ?? 1} · {user?.title || "Newbie Camper"}
            </div>
            <div className="text-[11px] font-bold" style={{ color: "var(--on-surface-muted)" }}>
              {xp.toLocaleString()} XP
              {!user?.is_max_level && nextXp && (
                <> · {(nextXp - xp).toLocaleString()} to {user?.next_level_title || "next"}</>
              )}
            </div>
          </div>
          <div className="w-full h-2 rounded-full overflow-hidden"
               style={{ background: "color-mix(in srgb, var(--brand) 10%, transparent)" }}>
            <div className="h-full rounded-full transition-all"
                 style={{
                   width: `${progressPct}%`,
                   background: "linear-gradient(90deg, var(--brand), var(--premium))",
                 }}
                 data-testid="profile-progress-bar" />
          </div>
        </div>
      </button>

      {/* Menus — mobile stacks all 3 sections; desktop puts the role
          tools + premium features side-by-side and lets the Account
          section span full width below them. */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6">
        {/* Role-specific main menu */}
        <MenuSection title={currentMode === "owner" ? "Owner tools" : "Camper tools"}>
          {roleMenu.map((it) => (
            <MenuRow key={it.label} item={it} isPremium={isPremium} onNavigateToPricing={() => navigate("/pricing")} />
          ))}
        </MenuSection>

        {/* Premium Features — visible to everyone, gated per-row */}
        <MenuSection
          title="Premium Features"
          badge={!isPremium ? "Upgrade to unlock" : null}
          testId="premium-menu"
        >
          {premiumMenu.map((it) => (
            <MenuRow
              key={it.label}
              item={{ ...it, premiumOnly: !isPremium }}
              isPremium={isPremium}
              onNavigateToPricing={() => navigate("/pricing")}
            />
          ))}
        </MenuSection>

        {/* Common footer menu — spans full width on desktop */}
        <div className="md:col-span-2">
          <MenuSection title="Account">
            <RoleToggleRow
              currentMode={currentMode}
              isCamper={isCamper}
              isOwner={isOwner}
              api={api}
              refresh={refresh}
              navigate={navigate}
            />
            {commonMenu.map((it) => (
              <MenuRow key={it.label} item={it} isPremium={isPremium} onNavigateToPricing={() => navigate("/pricing")} />
            ))}
            <button
              onClick={doLogout}
              className="w-full flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-black/5 transition text-left"
              data-testid="profile-logout"
            >
              <div className="w-8 h-8 rounded-lg flex items-center justify-center"
                   style={{ background: "color-mix(in srgb, #B45309 12%, transparent)", color: "#B45309" }}>
                <LogOut className="w-4 h-4" />
              </div>
              <span className="text-sm font-bold flex-1" style={{ color: "#B45309" }}>Logout</span>
            </button>
          </MenuSection>
        </div>
      </div>
    </div>
  );
}

function MenuSection({ title, badge, testId, children }) {
  return (
    <section className="card-elev p-3 md:p-4" data-testid={testId}>
      <div className="flex items-center justify-between px-2 pt-1 pb-2">
        <div className="text-[11px] font-extrabold uppercase tracking-widest" style={{ color: "var(--brand)" }}>
          {title}
        </div>
        {badge && (
          <span className="text-[9px] font-extrabold uppercase tracking-widest px-2 py-0.5 rounded-full"
                style={{ background: "color-mix(in srgb, var(--premium) 15%, transparent)", color: "var(--premium)" }}>
            {badge}
          </span>
        )}
      </div>
      <div className="flex flex-col gap-0.5">{children}</div>
    </section>
  );
}

function MenuRow({ item, isPremium, onNavigateToPricing }) {
  const { icon: Icon, label, to, premiumOnly, soon } = item;
  const gated = premiumOnly && !isPremium;

  const Wrap = ({ children, onClick }) => {
    if (soon) {
      return (
        <button
          className="w-full flex items-center gap-3 px-4 py-3 rounded-xl transition text-left opacity-60 cursor-not-allowed"
          data-testid={`profile-menu-${label.toLowerCase().replace(/\s+/g, "-")}`}
        >
          {children}
        </button>
      );
    }
    if (gated) {
      return (
        <button
          onClick={onNavigateToPricing}
          className="w-full flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-black/5 transition text-left"
          data-testid={`profile-menu-${label.toLowerCase().replace(/\s+/g, "-")}`}
        >
          {children}
        </button>
      );
    }
    if (!to) {
      return (
        <button
          onClick={onClick}
          className="w-full flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-black/5 transition text-left"
          data-testid={`profile-menu-${label.toLowerCase().replace(/\s+/g, "-")}`}
        >
          {children}
        </button>
      );
    }
    return (
      <Link to={to} className="flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-black/5 transition"
            data-testid={`profile-menu-${label.toLowerCase().replace(/\s+/g, "-")}`}>
        {children}
      </Link>
    );
  };

  return (
    <Wrap>
      <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0"
           style={{ background: "color-mix(in srgb, var(--brand) 12%, transparent)", color: "var(--brand)" }}>
        <Icon className="w-4 h-4" />
      </div>
      <span className="text-sm font-bold flex-1" style={{ color: "var(--on-surface)" }}>{label}</span>
      {gated && (
        <span className="text-[9px] font-extrabold uppercase tracking-widest px-2 py-0.5 rounded-full"
              style={{ background: "color-mix(in srgb, var(--premium) 15%, transparent)", color: "var(--premium)" }}>
          Premium
        </span>
      )}
      {soon && (
        <span className="text-[9px] font-extrabold uppercase tracking-widest px-2 py-0.5 rounded-full"
              style={{ background: "rgba(0,0,0,0.08)", color: "var(--on-surface-muted)" }}>
          Soon
        </span>
      )}
      {!gated && !soon && <ChevronRight className="w-4 h-4 opacity-40" />}
    </Wrap>
  );
}


// Inline "View as Camper / Owner" toggle rendered inside the Profile's
// Account card. Mirrors the header toggle (same POST /users/mode call,
// same refresh + redirect) so a user can flip roles from either place.
// Single-role users still see the row so they discover the feature; the
// role they don't own sits **disabled** on the segmented control.
function RoleToggleRow({ currentMode, isCamper, isOwner, api, refresh, navigate }) {
  const flip = async (mode) => {
    if (mode === currentMode) return;
    try {
      await api.post("/users/mode", { mode });
      const u = await refresh();
      const next = (u?.current_mode || mode) === "owner" ? "/owner" : "/passport";
      navigate(next, { replace: true });
    } catch { /* mode switch is best-effort */ }
  };
  const roles = [
    { id: "camper", label: "Camper", allowed: isCamper },
    { id: "owner", label: "Owner", allowed: isOwner },
  ];
  return (
    <div
      className="w-full flex items-center gap-3 px-4 py-3 rounded-xl"
      data-testid="profile-role-toggle"
    >
      <div
        className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0"
        style={{ background: "color-mix(in srgb, var(--brand) 12%, transparent)", color: "var(--brand)" }}
      >
        <Repeat className="w-4 h-4" />
      </div>
      <span className="text-sm font-bold flex-1" style={{ color: "var(--on-surface)" }}>
        View as
      </span>
      <div
        className="flex rounded-full p-0.5"
        style={{ background: "color-mix(in srgb, var(--brand) 8%, transparent)" }}
      >
        {roles.map((r) => {
          const active = currentMode === r.id;
          const disabled = !r.allowed;
          return (
            <button
              key={r.id}
              type="button"
              onClick={disabled ? undefined : () => flip(r.id)}
              disabled={disabled}
              aria-pressed={active}
              title={disabled ? `You don't have the ${r.label.toLowerCase()} role yet` : `View as ${r.label}`}
              data-testid={`profile-role-${r.id}`}
              className={`text-[11px] font-extrabold uppercase tracking-widest px-3 py-1 rounded-full transition ${disabled ? "cursor-not-allowed" : ""}`}
              style={{
                background: active ? "var(--brand-deep)" : "transparent",
                color: active ? "white" : (disabled ? "var(--on-surface-muted)" : "var(--brand)"),
                opacity: disabled && !active ? 0.5 : 1,
              }}
            >
              {r.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}
